import { useEffect, useRef } from "react";
import classNames from "classnames";
import type { RowData } from "@tanstack/react-table";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { InfiniteScrollSentinel } from "@design-system/InfiniteScrollSentinel/InfiniteScrollSentinel";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
// oxlint-disable-next-line no-restricted-imports -- ViewTable owns the GridTable implementation.
import { GridTableRow } from "@design-system/Tables/GridTable/GridTable";
import type { ColumnPinData } from "./ViewTable.lib";
import { isFillerColumn } from "./core/fillerColumns";
import { getLeafColumnHeaders } from "./core/getLeafColumnHeaders";
import type { ViewTableInstance } from "./core/table";
import { getPinClasses } from "./getPinClasses";
import { getSkeletonBarWidth } from "./getSkeletonBarWidth";
import { VIEW_TABLE_SCROLL_ROOT_SELECTOR } from "./viewTableScrollRoot";
import tableStyles from "./ViewTable.module.scss";
import styles from "./ViewTableLoadMore.module.scss";

/**
 * Opts a `ViewTable` into loading its next batch of rows on scroll. The table
 * renders the batch's placeholder and error rows; the caller owns fetching.
 */
export interface ViewTableLoadMore {
    hasMore: boolean;
    isLoading: boolean;
    isError: boolean;
    /** Shown in the error row, e.g. "Failed to load more transactions". */
    errorLabel: string;
    onLoadMore: () => void;
    onRetry: () => void;
    /** Changes when the list starts over; the table scrolls back to the top. */
    resetKey: string;
}

interface ViewTableLoadMoreRowsProps<TData extends RowData> {
    loadMore: ViewTableLoadMore;
    table: Pick<ViewTableInstance<TData>, "getLeafHeaders">;
    columnPinningMap: Map<string, ColumnPinData>;
    rowHeight: number;
}

const PLACEHOLDER_ROW_INDEXES = [0, 1, 2];
const SKELETON_HEIGHT = 16;
const SELECT_COLUMN_ID = "select";

const hasSkeleton = (columnId: string) =>
    columnId !== SELECT_COLUMN_ID && !isFillerColumn(columnId);

/**
 * The rows a load-more `ViewTable` renders below its data rows: a sentinel
 * that asks for the next batch near the bottom of the table's own scroll
 * container, shimmer rows while it loads, and a Retry row when it fails.
 * The table needs a bounded height (`fixedTopOffsetPx` or a parent with a
 * definite height); otherwise the sentinel stays in view and every batch
 * loads at once.
 */
export const ViewTableLoadMoreRows = <TData extends RowData>({
    loadMore,
    table,
    columnPinningMap,
    rowHeight,
}: ViewTableLoadMoreRowsProps<TData>) => {
    const { hasMore, isLoading, isError, errorLabel, onLoadMore, onRetry } =
        loadMore;
    const isShowingPlaceholders = hasMore && isLoading && !isError;
    const isShowingError = hasMore && isError;
    const sentinelWrapperRef = useRef<HTMLDivElement>(null);

    const previousResetKeyRef = useRef(loadMore.resetKey);
    useEffect(() => {
        if (previousResetKeyRef.current === loadMore.resetKey) {
            return;
        }
        previousResetKeyRef.current = loadMore.resetKey;
        sentinelWrapperRef.current
            ?.closest(VIEW_TABLE_SCROLL_ROOT_SELECTOR)
            ?.scrollTo({ top: 0 });
    }, [loadMore.resetKey]);

    const columns = isShowingPlaceholders
        ? getLeafColumnHeaders(table).map(({ column }) => ({
              id: column.id,
              minSize: column.columnDef.minSize ?? column.getSize(),
              pin: columnPinningMap.get(column.id),
              isAlignedRight: column.columnDef.meta?.align === "right",
          }))
        : [];

    return (
        <>
            <div ref={sentinelWrapperRef} className={styles.fullWidth}>
                <InfiniteScrollSentinel
                    onVisible={onLoadMore}
                    // A selector, not the table's ref: that ref is still
                    // empty when this child's effects first run.
                    scrollRootSelector={VIEW_TABLE_SCROLL_ROOT_SELECTOR}
                    disabled={!hasMore || isLoading || isError}
                />
            </div>
            <div role="status" className={styles.status}>
                {isShowingPlaceholders ? "Loading more…" : null}
            </div>
            {isShowingPlaceholders &&
                PLACEHOLDER_ROW_INDEXES.map((rowIndex) => (
                    <GridTableRow
                        key={rowIndex}
                        aria-hidden
                        className={classNames(
                            "view-table-row",
                            tableStyles.row,
                            tableStyles.topLevelRow,
                        )}
                        style={{ height: rowHeight }}
                    >
                        {columns.map((column, columnIndex) => (
                            <div
                                key={column.id}
                                className={classNames(
                                    column.id,
                                    tableStyles.cell,
                                    {
                                        ...getPinClasses(column.pin),
                                        [tableStyles.selectColumn]:
                                            column.id === SELECT_COLUMN_ID,
                                        [tableStyles.alignRight]:
                                            column.isAlignedRight,
                                    },
                                )}
                                style={column.pin?.style}
                            >
                                {hasSkeleton(column.id) && (
                                    <Skeleton
                                        width={getSkeletonBarWidth({
                                            minSize: column.minSize,
                                            columnIndex,
                                            rowIndex,
                                        })}
                                        height={SKELETON_HEIGHT}
                                    />
                                )}
                            </div>
                        ))}
                    </GridTableRow>
                ))}
            {isShowingError && (
                <GridTableRow>
                    <div className={styles.errorCell}>
                        <HStack
                            gap={8}
                            align="center"
                            className={styles.errorContent}
                        >
                            <span className={styles.errorLabel}>
                                {errorLabel}
                            </span>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onRetry}
                            >
                                Retry
                            </Button>
                        </HStack>
                    </div>
                </GridTableRow>
            )}
        </>
    );
};
